import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function PermAdmins() {
  const toast = useToast();
  const [servers, setServers] = useState([]);
  const [server, setServer] = useState("");
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ userid: "", discord_id: "" });
  // Linked-player catalogue for the autocomplete dropdown. Populated
  // once from `/admin/players/linked` — the same source of truth as
  // the Users admin page. Filtered on-typing by Discord username,
  // in-game name, or Player UID (steam_xxx / gdk_xxx).
  const [linked, setLinked] = useState([]);
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    api.get("/live/servers").then(({ data }) => {
      const list = data.servers || [];
      setServers(list);
      if (list.length > 0) setServer(list[0].name || list[0].slug);
    }).catch(() => {});
    api.get("/admin/players/linked")
      .then(({ data }) => setLinked(data.players || []))
      .catch(() => setLinked([]));
  }, []);

  const load = () => {
    if (!server) return;
    api.get(`/admin/perm/${encodeURIComponent(server)}`)
      .then(({ data }) => setAdmins(data.admins || []))
      .catch(() => setAdmins([]));
  };
  useEffect(() => { load(); }, [server]); // eslint-disable-line

  // Filter the catalogue down to the current server + matching query.
  // Sorted by most-recently-linked so the person you just linked is
  // at the top of the list.
  const suggestions = (() => {
    const q = query.trim().toLowerCase();
    return linked
      .filter((p) => !server || p.server_name === server)
      .filter((p) => {
        if (!q) return true;
        return (
          (p.discord_username || "").toLowerCase().includes(q) ||
          (p.player_name || "").toLowerCase().includes(q) ||
          (p.player_userid || "").toLowerCase().includes(q) ||
          String(p.user_id || "").toLowerCase().includes(q)
        );
      })
      .slice(0, 12);
  })();

  const pickSuggestion = (p) => {
    setForm({
      userid: p.player_userid || "",
      // Only the discord-linked rows carry a numeric Discord ID; web-only
      // links use the web_user_id which the RCON side doesn't need.
      discord_id: p.source === "discord" ? String(p.user_id || "") : "",
    });
    setQuery(p.discord_username || p.player_name || p.player_userid || "");
    setShowSuggestions(false);
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/perm/setadmin", {
        server_name: server,
        userid: form.userid,
        discord_id: form.discord_id ? parseInt(form.discord_id, 10) : null,
      });
      if (data?.ok) {
        toast.success(`Granted admin to ${form.userid} — applied in-game`);
      } else {
        toast.success(`Saved — will apply on next login. Server said: ${data?.rcon_response || "player not found online"}`);
      }
      setForm({ userid: "", discord_id: "" });
      setQuery("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (uid) => {
    if (!window.confirm(`Revoke permanent admin from ${uid}?`)) return;
    try {
      await api.delete(`/admin/perm/${encodeURIComponent(server)}/${encodeURIComponent(uid)}`);
      toast.success("Removed (won't re-grant on next login)");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-up" data-testid="perm-admins-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Permanent In-game Admins</h3>
        <p className="text-xs text-[var(--muted)]">
          Players added here get <code>setadmin</code> auto-applied via RCON every time they log in (the Discord bot handles the trigger). Mirrors <code>/admin setadmin</code> / <code>/admin removeadmin</code>.
        </p>
      </div>

      <Field label="Server">
        <select className="input" value={server} onChange={(e) => setServer(e.target.value)} data-testid="perm-server-select">
          {servers.map((s) => <option key={s.slug} value={s.name || s.slug}>{s.name || s.slug}</option>)}
        </select>
      </Field>

      <form onSubmit={add} className="glass-card p-5 space-y-3" data-testid="perm-add-form">
        <h3 className="label">Grant Permanent Admin</h3>

        {/* Player search — pull from the linked-players catalogue so
            the operator doesn't need to memorise steam_/gdk_ UIDs.
            Selecting a suggestion auto-fills both the Player UID
            and Discord ID fields below. */}
        <Field
          label="Search linked players"
          hint="Type a Discord username, in-game name, or Player UID — matching linked players for this server appear below. Click one to auto-fill."
        >
          <div className="relative">
            <input
              type="text"
              className="input"
              placeholder="e.g. Repoking79, Chillet, steam_1234..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              data-testid="perm-search-input"
            />
            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="absolute z-20 left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto rounded-md border border-white/10 bg-[var(--panel)] shadow-lg"
                data-testid="perm-search-suggestions"
              >
                {suggestions.map((p) => (
                  <li
                    key={`${p.source}-${p.user_id}-${p.player_userid}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickSuggestion(p)}
                    className="px-3 py-2 hover:bg-[var(--teal-soft)] cursor-pointer text-sm border-b border-white/5 last:border-b-0"
                    data-testid={`perm-suggestion-${p.player_userid}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate flex items-center gap-1.5">
                        <Users size={12} className="opacity-60" />
                        {p.discord_username || p.player_name || "(unnamed)"}
                      </span>
                      <span className={
                        "text-[9px] font-mono px-1.5 py-0.5 rounded flex-shrink-0 " +
                        (p.source === "discord"
                          ? "bg-indigo-500/20 text-indigo-300"
                          : "bg-emerald-500/20 text-emerald-300")
                      }>
                        {p.source === "discord" ? "DISCORD" : "WEB"}
                      </span>
                    </div>
                    <div className="text-[10px] text-[var(--muted)] font-mono truncate mt-0.5">
                      {p.player_userid || "(no UID)"}
                      {p.player_name && p.player_name !== p.discord_username && (
                        <span className="ml-2 text-[var(--teal-soft)]">
                          IGN: {p.player_name}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {showSuggestions && query && suggestions.length === 0 && (
              <div
                className="absolute z-20 left-0 right-0 top-full mt-1 rounded-md border border-white/10 bg-[var(--panel)] px-3 py-2 text-xs text-[var(--muted)] italic"
                data-testid="perm-search-empty"
              >
                No linked players match. You can still type the Player UID directly below.
              </div>
            )}
          </div>
        </Field>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Player UID (steam_xxx / gdk_xxx)"><input className="input font-mono text-xs" required value={form.userid} onChange={(e) => setForm({ ...form, userid: e.target.value })} data-testid="perm-userid" /></Field>
          <Field label="Discord ID (optional)"><input className="input font-mono text-xs" type="number" value={form.discord_id} onChange={(e) => setForm({ ...form, discord_id: e.target.value })} data-testid="perm-discord-id" /></Field>
        </div>
        <button className="btn" data-testid="perm-add-submit">Add + RCON setadmin now</button>
      </form>

      <div className="glass-card p-5" data-testid="perm-list">
        <h3 className="label mb-3">Current ({admins.length})</h3>
        {admins.length === 0 && <p className="text-sm text-[var(--muted)]">No permanent admins on this server.</p>}
        <ul className="divide-y divide-white/5">
          {admins.map((a) => {
            // Cross-reference with the linked-players catalogue so
            // the operator sees a real name next to each UID, not
            // just the raw steam_/gdk_ string.
            const linkedRow = linked.find(
              (p) => p.player_userid === a.userid && p.server_name === server,
            );
            const displayName = linkedRow?.discord_username || linkedRow?.player_name || null;
            return (
              <li key={a.userid} className="flex items-center justify-between py-2 text-sm">
                <span className="flex flex-col min-w-0 flex-1 pr-2">
                  {displayName && (
                    <span className="font-medium truncate">{displayName}</span>
                  )}
                  <span className="font-mono text-xs truncate">{a.userid}</span>
                  <span className="text-[10px] text-[var(--muted)]">
                    {a.discord_id ? `Discord ID: ${a.discord_id} · ` : ""}Added {a.added_at}
                  </span>
                  <span
                    className={`text-[10px] font-medium ${a.applied_at ? "text-emerald-400" : "text-amber-400"}`}
                    data-testid={`perm-status-${a.userid}`}
                  >
                    {a.applied_at
                      ? `✅ Applied in-game ${String(a.applied_at).slice(0, 16).replace("T", " ")} UTC`
                      : "🕓 Pending — applies on next login (bot re-grants within ~60s)"}
                  </span>
                </span>
                <button onClick={() => remove(a.userid)} className="btn btn-sm btn-danger" data-testid={`perm-remove-${a.userid}`}>Revoke</button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Whitelist
// ---------------------------------------------------------------------------
